import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING = {
  role: "assistant",
  text: "Hello! I'm the RK travel assistant. Ask me about destinations, visit visas, or packages — I'll point you the right way.",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(
    typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}`
  );
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "visitor", text }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: text }),
      });
      if (!resp.ok || !resp.body) throw new Error("Chat unavailable");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            const { text: delta } = JSON.parse(payload);
            if (delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "I'm having trouble connecting right now — please reach us on WhatsApp at +971 50 645 7321 and we'll help immediately.",
        };
        return copy;
      });
      toast.error("Assistant connection issue — WhatsApp is always available.");
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-toggle-button"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.2, duration: 0.8 }}
        onClick={() => setOpen((o) => !o)}
        aria-label="Open AI travel assistant"
        className="fixed bottom-24 md:bottom-6 left-4 md:left-6 z-50 rounded-full p-[2.5px] bg-gradient-to-r from-cyan-400 via-lime-400 to-yellow-400 shadow-[0_8px_32px_rgba(0,0,0,0.5)] hover:scale-105 transition-transform duration-300"
      >
        <span className="flex items-center gap-2 rounded-full bg-[#0A1128]/95 backdrop-blur-xl px-5 py-3">
          <Sparkles size={16} className="text-slate-50" />
          <span className="text-sm font-medium tracking-[0.1em] text-slate-50">AI</span>
        </span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-40 md:bottom-24 left-4 md:left-6 z-50 w-[calc(100vw-2rem)] max-w-sm h-[480px] flex flex-col backdrop-blur-xl bg-[#101C3E]/95 border border-[#d4af37]/30 shadow-[0_8px_40px_rgba(0,0,0,0.6)]"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#d4af37]/20">
              <div>
                <p className="font-display text-lg text-slate-50">RK Travel Assistant</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#d4af37] mt-0.5">
                  Powered by AI · ChatGPT
                </p>
              </div>
              <button
                data-testid="chat-close-button"
                onClick={() => setOpen(false)}
                aria-label="Close assistant"
                className="text-slate-400 hover:text-[#d4af37] transition-colors p-1"
              >
                <X size={18} />
              </button>
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-message-${i}`}
                  className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                    m.role === "visitor"
                      ? "ml-auto bg-[#d4af37] text-[#0A1128]"
                      : "bg-[#0A1128] border border-[#d4af37]/20 text-slate-200"
                  }`}
                >
                  {m.text || <Loader2 size={14} className="animate-spin text-[#d4af37]" />}
                </div>
              ))}
            </div>

            <div className="px-4 py-4 border-t border-[#d4af37]/20 flex gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about visas, destinations…"
                className="flex-1 bg-[#0A1128] border border-[#d4af37]/25 px-4 py-3 text-sm text-slate-50 placeholder:text-slate-400 focus:outline-none focus:border-[#d4af37] transition-colors"
              />
              <button
                data-testid="chat-send-button"
                onClick={send}
                disabled={streaming || !input.trim()}
                aria-label="Send message"
                className="w-12 bg-[#d4af37] text-[#0A1128] flex items-center justify-center hover:bg-[#f3e5ab] transition-colors disabled:opacity-50"
              >
                {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
